import axios from "axios";
import { API } from "../../backend";
import authHeader from "../auth-header";

/**
 * Switch company by getting a fresh JWT token for the target company's user.
 * Saves original user to localStorage so we can switch back.
 * Replaces entire user session with the new company's data.
 */
const switchCompany = (company_id) => {
  return axios
    .post(API + "company/switch/", { company_id }, { headers: authHeader() })
    .then((response) => {
      if (response.data.success === 1) {
        const d = response.data;

        // Save current session so user can switch back
        const currentUser = localStorage.getItem("user");
        if (currentUser) {
          localStorage.setItem("original_user",         currentUser);
          localStorage.setItem("original_companyid",    localStorage.getItem("companyid"));
          localStorage.setItem("original_company_name", localStorage.getItem("active_company_name"));
        }

        // Replace entire localStorage with the switched company's session
        // (same shape as login response — all existing code works unchanged)
        const newUser = {
          access:   d.access,
          refresh:  d.refresh,
          companyid: d.companyid,
          username: d.username,
          locale:   d.locale,
          currency: d.currency,
          parent:   d.parent,
          permissions: d.permissions,
          groups:   d.groups,
          user_id:  d.user_id,
          active_company_id:   d.active_company_id,
          active_company_name: d.active_company_name,
          accessible_companies: d.accessible_companies,
        };

        localStorage.setItem("user",                  JSON.stringify(newUser));
        localStorage.setItem("companyid",             d.companyid);
        localStorage.setItem("username",              JSON.stringify(d.username));
        localStorage.setItem("locale",                d.locale);
        localStorage.setItem("currency",              d.currency);
        localStorage.setItem("parent",                d.parent);
        localStorage.setItem("permissions",           d.permissions);
        localStorage.setItem("userid",                d.user_id);
        localStorage.setItem("active_company_id",     d.active_company_id);
        localStorage.setItem("active_company_name",   d.active_company_name);
        localStorage.setItem("managed_clients_count", d.managed_clients_count);
        localStorage.setItem("accessible_companies",  JSON.stringify(d.accessible_companies));
        // Store switched_from so frontend can show "Switch back to ABC" button
        localStorage.setItem("switched_from_company_id",   d.switched_from_company_id);
        localStorage.setItem("switched_from_company_name", d.switched_from_company_name);
      }
      return response.data;
    });
};

/**
 * Switch back to the original company using the saved original_user session.
 */
const switchBack = () => {
  const originalUser = localStorage.getItem("original_user");
  if (!originalUser) return Promise.resolve(null);

  const parsed = JSON.parse(originalUser);

  // Restore all original localStorage keys
  localStorage.setItem("user",                 originalUser);
  localStorage.setItem("companyid",            parsed.companyid);
  localStorage.setItem("username",             JSON.stringify(parsed.username));
  localStorage.setItem("locale",               parsed.locale);
  localStorage.setItem("currency",             parsed.currency);
  localStorage.setItem("parent",               parsed.parent);
  localStorage.setItem("permissions",          parsed.permissions);
  localStorage.setItem("userid",               parsed.user_id);
  localStorage.setItem("active_company_id",    parsed.active_company_id  || parsed.companyid);
  localStorage.setItem("active_company_name",  parsed.active_company_name || "");
  localStorage.setItem("accessible_companies", JSON.stringify(parsed.accessible_companies || []));

  // Clear switch-back keys
  localStorage.removeItem("original_user");
  localStorage.removeItem("original_companyid");
  localStorage.removeItem("original_company_name");
  localStorage.removeItem("switched_from_company_id");
  localStorage.removeItem("switched_from_company_name");

  return Promise.resolve(parsed);
};

/**
 * Returns true if currently viewing a switched company (not own company).
 */
const isSwitched = () => {
  return !!localStorage.getItem("original_user");
};

/**
 * Get all companies this user can access (own + clients).
 */
const getAccessibleCompanies = () => {
  try {
    return JSON.parse(localStorage.getItem("accessible_companies")) || [];
  } catch {
    return [];
  }
};

/**
 * Get currently active company info from localStorage.
 */
const getActiveCompany = () => {
  return {
    id:   localStorage.getItem("active_company_id"),
    name: localStorage.getItem("active_company_name"),
  };
};

/**
 * Fetch fresh list of clients from API.
 */
const listMyClients = () => {
  return axios.get(API + "company/my-clients/", { headers: authHeader() });
};

const CompanySwitchService = {
  switchCompany,
  switchBack,
  isSwitched,
  getAccessibleCompanies,
  getActiveCompany,
  listMyClients,
};

export default CompanySwitchService;